/**
 * ChatSidebarEscapeHandler
 *
 * Closes the chat sidebar on Escape, but defers to the command palette when
 * it owns the Escape key (so closing the palette doesn't also close the
 * assistant).
 *
 * Extracted into its own component so the palette open-state subscription
 * (`usePaletteIsOpen`) doesn't re-render the full designer subtree on every
 * ⌘K toggle. This component renders nothing.
 */
import { useEffect } from 'react';
import { usePaletteIsOpen } from '@/components/GlobalNavPalette';
import { shouldSkipShortcut } from '@/utils/input';

interface ChatSidebarEscapeHandlerProps {
  /** Only listens when true; the assistant panel is only mounted in designer view. */
  enabled: boolean;
  isChatSidebarOpen: boolean;
  onClose: () => void;
}

export function ChatSidebarEscapeHandler({
  enabled,
  isChatSidebarOpen,
  onClose,
}: ChatSidebarEscapeHandlerProps): null {
  const isCommandPaletteOpen = usePaletteIsOpen();

  useEffect(() => {
    if (!enabled || !isChatSidebarOpen) return;

    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (isCommandPaletteOpen) return;
      if (shouldSkipShortcut(e)) return;
      e.preventDefault();
      if (e.repeat) return;
      onClose();
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [enabled, isChatSidebarOpen, isCommandPaletteOpen, onClose]);

  return null;
}
